import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "../lib/api.js";
import { formatBillingDate } from "../lib/formatBillingDate.js";

/** What /session says about keeping a PIX subscription whose renewal was stopped (PIT-461). */
export interface PixKeepState {
  enabled: true;
  scheduled_change_id: string;
  eligibility: "ok" | "too_close" | "too_early";
  first_charge_at: string | null;
  available_from: string | null;
  pending_order_id: string | null;
}

export interface KeepAuthorization {
  orderId: string;
  emv: string | null;
}

type Blocked = { kind: "too_close" } | { kind: "too_early"; availableFrom: string | null };

/**
 * "Manter assinatura" on PIX. The stopped agreement cannot come back, so keeping means authorizing
 * a new Pix Automático in the bank app: nothing charged today, the first charge at the end of the
 * paid period. The dialog says that before asking, and says why when the bank cannot schedule it
 * (period ending in under 3 days, or more than a month ahead).
 */
export function KeepPixSubscriptionDialog({
  token,
  changeId,
  effectiveAt,
  keep,
  onAuthorization,
  onSupport,
  onClose,
}: {
  token: string;
  changeId: string;
  effectiveAt: string;
  keep: PixKeepState;
  onAuthorization: (auth: KeepAuthorization) => void;
  onSupport: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(() =>
    keep.eligibility === "too_close"
      ? { kind: "too_close" }
      : keep.eligibility === "too_early"
        ? { kind: "too_early", availableFrom: keep.available_from }
        : null,
  );

  async function authorize() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ order: { id: string; pix_emv: string | null } }>(
        `/api/integrated/scheduled-changes/${changeId}/keep`,
        { method: "POST", body: JSON.stringify({ token }) },
      );
      onAuthorization({ orderId: r.order.id, emv: r.order.pix_emv });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; available_from?: string }) : null;
      if (body?.error === "keep_too_close") setBlocked({ kind: "too_close" });
      else if (body?.error === "keep_too_early") setBlocked({ kind: "too_early", availableFrom: body.available_from ?? null });
      else if (body?.error === "invalid_cpf") setError(t("integrated.keepPix.invalidCpf"));
      else if (body?.error === "keep_in_progress") setError(t("integrated.keepPix.inProgress"));
      // Keeping is a PIX authorization: no card to offer, only to try again later.
      else if (body?.error === "pix_unavailable") setError(t("integrated.pixUnavailableRetry"));
      else setError(t("integrated.changeFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  const periodEnd = formatBillingDate(effectiveAt, i18n.language);
  const body = blocked
    ? blocked.kind === "too_close"
      ? t("integrated.keepPix.tooClose", { date: periodEnd })
      : t("integrated.keepPix.tooEarly", {
          date: blocked.availableFrom ? formatBillingDate(blocked.availableFrom, i18n.language) : periodEnd,
        })
    : t("integrated.keepPix.body", {
        date: keep.first_charge_at ? formatBillingDate(keep.first_charge_at, i18n.language) : periodEnd,
      });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{t("integrated.keepPix.title")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">{body}</p>
        {!blocked && keep.pending_order_id && (
          <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.keepPix.alreadyRequested")}</p>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex flex-wrap gap-2 justify-end pt-1">
          {blocked ? (
            <>
              <button type="button" className="btn-ghost" onClick={onSupport}>
                {t("integrated.keepPix.support")}
              </button>
              <button type="button" className="btn-primary" onClick={onClose}>
                {t("integrated.keepPix.understood")}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
                {t("common.back")}
              </button>
              <button type="button" className="btn-primary" disabled={submitting} onClick={() => void authorize()}>
                {submitting
                  ? t("integrated.processing")
                  : keep.pending_order_id
                    ? t("integrated.keepPix.showAuthorization")
                    : t("integrated.keepPix.authorize")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
